import { useMemo, useState } from "react";
import {
  TournamentEventSchedule,
  TournamentEventScheduleActivity,
  TournamentEventScheduleActivityType,
  TournamentEventScheduleDay,
} from "../services/challengeAdminService";
import "./TournamentEventScheduleView.css";

type TournamentEventScheduleTexts = {
  title: string;
  summaryTitle: string;
  dayTime: string;
  activities: string;
  gamesActivities: string;
  courts: string;
  legend: string;
  now: string;
  next: string;
  noLocation: string;
  noTime: string;
  timeSeparator: string;
  activityTypes: Record<TournamentEventScheduleActivityType, string>;
};

type TournamentEventScheduleViewProps = {
  schedule: TournamentEventSchedule;
  texts: TournamentEventScheduleTexts;
};

const activityMeta: Record<
  TournamentEventScheduleActivityType,
  { icon: string; tone: string }
> = {
  games: { icon: "sports_tennis", tone: "purple" },
  opening: { icon: "flag", tone: "blue" },
  checkin: { icon: "how_to_reg", tone: "blue" },
  food: { icon: "restaurant", tone: "amber" },
  coffee: { icon: "local_cafe", tone: "amber" },
  cake: { icon: "cake", tone: "pink" },
  exercise: { icon: "fitness_center", tone: "green" },
  draw: { icon: "casino", tone: "pink" },
  awards: { icon: "trophy", tone: "green" },
  celebration: { icon: "celebration", tone: "green" },
  break: { icon: "pause_circle", tone: "muted" },
  general: { icon: "event", tone: "blue" },
  other: { icon: "stars", tone: "pink" },
};

function localTodayValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getDate()).padStart(2, "0")}`;
}

function minutesFromTime(value?: string | null) {
  const match = String(value || "").match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function currentMinutes() {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

function formatDayButton(day: TournamentEventScheduleDay) {
  const [year, month, date] = day.date.split("-").map(Number);
  if (!year || !month || !date) return day.title || day.date;
  const dayDate = new Date(year, month - 1, date);
  const weekday = new Intl.DateTimeFormat("pt-BR", { weekday: "short" })
    .format(dayDate)
    .replace(".", "");
  return `${weekday} ${String(date).padStart(2, "0")}/${String(month).padStart(
    2,
    "0",
  )}`;
}

function resolveActivityLabel(
  activity: TournamentEventScheduleActivity,
  texts: TournamentEventScheduleTexts,
) {
  if (activity.type === "other" && activity.customTypeName) {
    return activity.customTypeName;
  }
  return texts.activityTypes[activity.type] || texts.activityTypes.general;
}

function resolveActivityState(
  day: TournamentEventScheduleDay,
  activity: TournamentEventScheduleActivity,
  nextActiveId: string,
) {
  const today = localTodayValue();
  if (day.date !== today) return "";
  const start = minutesFromTime(activity.startTime);
  const end = minutesFromTime(activity.endTime);
  const now = currentMinutes();
  if (start !== null && end !== null && start <= now && now <= end) return "now";
  if (activity.id === nextActiveId) return "next";
  if (end !== null && end < now) return "ended";
  if (start !== null && start < now && !activity.endTime) return "ended";
  return "";
}

export default function TournamentEventScheduleView({
  schedule,
  texts,
}: TournamentEventScheduleViewProps) {
  const days = useMemo(
    () =>
      (schedule.days || [])
        .filter((day) => day.isActive && day.activities.some((item) => item.isActive))
        .sort(
          (a, b) =>
            a.date.localeCompare(b.date) ||
            a.displayOrder - b.displayOrder ||
            a.id.localeCompare(b.id),
        )
        .map((day) => ({
          ...day,
          activities: day.activities
            .filter((item) => item.isActive)
            .sort(
              (a, b) =>
                a.startTime.localeCompare(b.startTime) ||
                a.displayOrder - b.displayOrder ||
                a.id.localeCompare(b.id),
            ),
        })),
    [schedule.days],
  );
  const [selectedDayId, setSelectedDayId] = useState(() => {
    const today = localTodayValue();
    return days.find((day) => day.date === today)?.id || days[0]?.id || "";
  });
  const selectedDay = days.find((day) => day.id === selectedDayId) || days[0];

  const nextActiveId = useMemo(() => {
    if (!selectedDay || selectedDay.date !== localTodayValue()) return "";
    const now = currentMinutes();
    return (
      selectedDay.activities.find((activity) => {
        const start = minutesFromTime(activity.startTime);
        return start !== null && start >= now;
      })?.id || ""
    );
  }, [selectedDay]);

  if (!selectedDay) return null;

  const courts = Array.from(
    new Set(
      selectedDay.activities
        .map((activity) => activity.locationLabel || "")
        .filter(Boolean),
    ),
  );
  const gamesActivitiesCount = selectedDay.activities.filter(
    (activity) => activity.type === "games",
  ).length;
  const legend = Array.from(
    new Map(
      selectedDay.activities.map((activity) => [
        activity.type,
        {
          type: activity.type,
          meta: activityMeta[activity.type] || activityMeta.general,
        },
      ]),
    ).values(),
  ).filter((item) => item.type !== "general");

  return (
    <section className="tournament-event-schedule challenge-public-panel">
      <header className="challenge-public-panel-head tournament-event-schedule-head">
        <h3>
          <span className="challenge-public-panel-title-icon">
            <span className="material-symbols-outlined" aria-hidden="true">
              calendar_month
            </span>
          </span>
          <span>{texts.title}</span>
        </h3>
      </header>

      <div className="tournament-event-schedule-days" role="tablist">
        {days.map((day) => (
          <button
            type="button"
            key={day.id}
            className={`tournament-event-schedule-day${
              day.id === selectedDay.id ? " is-active" : ""
            }`}
            onClick={() => setSelectedDayId(day.id)}
          >
            {day.title || formatDayButton(day)}
          </button>
        ))}
      </div>

      <div className="tournament-event-schedule-grid">
        <div className="tournament-event-schedule-list">
          {selectedDay.activities.map((activity) => {
            const meta = activityMeta[activity.type] || activityMeta.general;
            const state = resolveActivityState(selectedDay, activity, nextActiveId);
            return (
              <article
                key={activity.id}
                className={`tournament-event-schedule-item tournament-event-schedule-item--${meta.tone}${
                  state ? ` is-${state}` : ""
                }${activity.isHighlighted ? " is-highlighted" : ""}`}
              >
                <time className="tournament-event-schedule-time">
                  {activity.startTime || texts.noTime}
                  {activity.endTime ? <span>{activity.endTime}</span> : null}
                </time>
                <span className="tournament-event-schedule-dot">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    {meta.icon}
                  </span>
                </span>
                <div className="tournament-event-schedule-content">
                  <div className="tournament-event-schedule-title-row">
                    <h4>{activity.title}</h4>
                    {state === "now" ? <strong>{texts.now}</strong> : null}
                    {state === "next" ? <strong>{texts.next}</strong> : null}
                  </div>
                  {activity.type !== "general" ||
                  activity.categoryLabel ||
                  activity.locationLabel ? (
                    <div className="tournament-event-schedule-badges">
                      {activity.type !== "general" ? (
                        <span>{resolveActivityLabel(activity, texts)}</span>
                      ) : null}
                      {activity.categoryLabel ? <span>{activity.categoryLabel}</span> : null}
                      {activity.locationLabel ? <span>{activity.locationLabel}</span> : null}
                    </div>
                  ) : null}
                  {activity.description ? <p>{activity.description}</p> : null}
                </div>
              </article>
            );
          })}
        </div>

        <aside className="tournament-event-schedule-summary">
          <h4>{texts.summaryTitle}</h4>
          <dl>
            <div>
              <dt>{texts.dayTime}</dt>
              <dd>
                {selectedDay.startTime && selectedDay.endTime
                  ? `${selectedDay.startTime} ${texts.timeSeparator} ${selectedDay.endTime}`
                  : texts.noTime}
              </dd>
            </div>
            <div>
              <dt>{texts.activities}</dt>
              <dd>{selectedDay.activities.length}</dd>
            </div>
            {gamesActivitiesCount > 0 ? (
              <div>
                <dt>{texts.gamesActivities}</dt>
                <dd>{gamesActivitiesCount}</dd>
              </div>
            ) : null}
            {courts.length > 0 ? (
              <div>
                <dt>{texts.courts}</dt>
                <dd>{courts.join(", ")}</dd>
              </div>
            ) : null}
          </dl>
          {legend.length > 0 ? (
            <div className="tournament-event-schedule-legend">
              <span>{texts.legend}</span>
              {legend.map((item) => (
                <em key={item.type} className={`is-${item.meta.tone}`}>
                  {texts.activityTypes[item.type] || texts.activityTypes.general}
                </em>
              ))}
            </div>
          ) : null}
        </aside>
      </div>
    </section>
  );
}
